import { z } from 'zod';

/**
 * Shared primitives used across every contract. Master Spec §6, §7, §9.
 * These schemas are the single vocabulary — no work package invents its own
 * variant (§8.2).
 */

export const uuid = z.string().uuid();
export const isoDateTime = z.string().datetime({ offset: true });

/** Recursive JSON value — used for opaque JSONB payloads that cross boundaries. */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export const jsonValue: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValue),
    z.record(jsonValue),
  ]),
);

export const jsonObject = z.record(jsonValue);

/** Tool side-effect classification (§9.2). Governs retry policy (I8/§9.4). */
export const sideEffect = z.enum(['none', 'idempotent', 'non_idempotent']);
export type SideEffect = z.infer<typeof sideEffect>;

/** Risk level surfaced on approvals (§9.2/§10). */
export const riskLevel = z.enum(['low', 'medium', 'high']);
export type RiskLevel = z.infer<typeof riskLevel>;

/** Per-tool autonomy (D9). v1 has exactly two levels. */
export const autonomy = z.enum(['allowed', 'requires_approval']);
export type Autonomy = z.infer<typeof autonomy>;

/** Workspace membership roles (D9). */
export const memberRole = z.enum(['owner', 'member']);
export type MemberRole = z.infer<typeof memberRole>;

/** Run lifecycle states (§7.1). */
export const runStatus = z.enum([
  'QUEUED',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'LIMIT_EXCEEDED',
  // A research run (§13.1, WP-R2) that hit its time/cost `limit` and terminated with
  // a best-so-far report. Distinct from `LIMIT_EXCEEDED` (the per-run authoritative
  // budget guard, §7.4): `LIMIT_REACHED` is the EXPECTED, graceful end of a research
  // run — the platform asked the agent to finalise as the budget wound down and it
  // did. Terminal like the others; carries a persisted best-so-far report artifact.
  'LIMIT_REACHED',
  'BLOCKED',
  'WAITING_FOR_APPROVAL',
  'WAITING',
]);
export type RunStatus = z.infer<typeof runStatus>;

export const TERMINAL_RUN_STATUSES = [
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'LIMIT_EXCEEDED',
  'LIMIT_REACHED',
] as const;

/**
 * Outcome status partitions (§14, #263). These partition ALL of `runStatus` into three
 * disjoint groups used to derive a run's outcome (see `deriveRunOutcome`). Kept here next
 * to `runStatus` so a new status must be classified here too (exhaustiveness):
 *   - ACTIVE  — still running; a live run is never hidden by the outcome filter.
 *   - FAILED  — terminated unsuccessfully.
 *   - SUCCESS — terminated successfully; `result` vs `no_result` then depends on signals.
 */
export const ACTIVE_RUN_STATUSES = [
  'QUEUED',
  'RUNNING',
  'WAITING',
  'WAITING_FOR_APPROVAL',
] as const;
export const FAILED_RUN_STATUSES = [
  'FAILED',
  'CANCELLED',
  'LIMIT_EXCEEDED',
  'BLOCKED',
] as const;
export const SUCCESS_RUN_STATUSES = ['COMPLETED', 'LIMIT_REACHED'] as const;
